import React from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { useOrbitContext } from '../OrbitContext';
import { TOrbit } from '../i18n/TModules';
import { OrbitPaging } from '../pagination';
import { ORBIT_GLOBAL_STARTERS, isStarterAllowed } from '../promptStarters';
import { OrbitTextButton, orbitTokens, orbitType } from '../styles';
import useReportsGrant from '../useReportsGrant';

export interface IOrbitPagingFooterProps {
  paging: OrbitPaging;
  testId: string;
}

const Footer = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-top: 6px;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-left: 12px;
  }
`;

const Count = styled.span`
  color: ${orbitTokens.textMuted};
  ${orbitType.helping}
`;

const Capped = styled.span`
  color: ${orbitTokens.textMuted};
  ${orbitType.helping}
  text-align: right;
`;

const REPORT_STARTER = ORBIT_GLOBAL_STARTERS.find(starter => starter.id === 'global.report');

/**
 * Split out so the footer itself stays a presentational leaf: only the capped state, which needs to
 * send a question, reaches into the Orbit context.
 */
const ReportButton: React.FC<{ testId: string }> = ({ testId }) => {
  const { t } = useMultiLanguageTranslation();
  const { ask, isAsking } = useOrbitContext();
  const reports = useReportsGrant();

  // The only starter this footer can draw is the report one, which turns on the Reports grant
  // alone; no card on screen is consulted, so the segment grant is not one it can answer for.
  if (
    REPORT_STARTER === undefined ||
    !isStarterAllowed(REPORT_STARTER, { reports, segmentDimension: false })
  ) {
    return null;
  }

  const label = t(REPORT_STARTER.labelKey);

  return (
    <OrbitTextButton
      type="button"
      disabled={isAsking}
      data-testid={testId}
      onClick={() => ask(label)}
    >
      {label}
    </OrbitTextButton>
  );
};

/**
 * `Showing 1-n of X` plus the control that grows the page, or — once the render cap is reached —
 * the Generate Report route the FR sends large result sets down.
 */
const OrbitPagingFooter: React.FC<IOrbitPagingFooterProps> = ({ paging, testId }) => {
  const { t } = useMultiLanguageTranslation();

  if (!paging.paginated) {
    return null;
  }

  return (
    <Footer data-testid={testId}>
      <Count data-testid={`${testId}-count`}>
        {t(`${TOrbit}.paging.showing`, { shown: paging.shown, total: paging.total })}
      </Count>
      {paging.canShowMore ? (
        <OrbitTextButton
          type="button"
          data-testid={`${testId}-show-more`}
          onClick={paging.showMore}
        >
          {t(`${TOrbit}.paging.showMore`)}
        </OrbitTextButton>
      ) : null}
      {paging.atCap ? (
        <Capped data-testid={`${testId}-capped`}>
          {t(`${TOrbit}.paging.capped`)} <ReportButton testId={`${testId}-report`} />
        </Capped>
      ) : null}
    </Footer>
  );
};

export default OrbitPagingFooter;
